import { ReactNode, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "./RyeIcon";
import { cardRiseDelay } from "./riseIn";

/**
 * The look shared by cards inside a panel: white, with a quieter version of
 * the panel's warm outline and a faint shadow. Use RyeCard where you can; use
 * this directly for cards that need their own structure, like draggable ones.
 */
export const RYE_CARD_SURFACE = buildClasses(
  "rounded-xl",
  "border",
  "bg-white",
  "border-edge/[0.08]",
  "border-b-edge/[0.12]",
  "shadow-card",
);

/**
 * A card inside a panel, with an optional header. The header's title is plain
 * by default; "accent" leads it with a small purple icon and title, for cards
 * that introduce something, like Home's.
 */
export function RyeCard({
  title,
  label,
  icon,
  headerStyle = "plain",
  trailing,
  fillHeight = false,
  rise = true,
  children,
}: {
  title?: string;
  /** Names the card for assistive tech when it has no title. */
  label?: string;
  /** Only shown with the "accent" header. */
  icon?: string;
  headerStyle?: "plain" | "accent";
  /** Shown at the right end of the header, like a total. */
  trailing?: ReactNode;
  /** Stretches the body to fill a card that's taller than its content. */
  fillHeight?: boolean;
  /**
   * Rises out of the panel when it first appears. Turn it off for stand-ins,
   * like skeletons, that the real card replaces, so it doesn't rise twice.
   */
  rise?: boolean;
  children?: ReactNode;
}): JSX.Element {
  const accent = headerStyle === "accent";
  const [riseDelay] = useState(() => (rise ? cardRiseDelay() : null));
  return (
    <section
      aria-label={label ?? title}
      className={buildClasses(
        RYE_CARD_SURFACE,
        "p-5",
        { if: fillHeight, then: buildClasses("flex", "flex-col") },
        { if: riseDelay !== null, then: "motion-safe:animate-rise" },
      )}
      style={
        riseDelay !== null ? { animationDelay: `${riseDelay}ms` } : undefined
      }
    >
      {title != null && (
        <div
          className={buildClasses(
            "flex",
            "items-baseline",
            "justify-between",
            "gap-4",
            { if: children != null, then: accent ? "mb-3" : "mb-2" },
          )}
        >
          <div
            className={buildClasses("flex", "items-center", "gap-2", {
              if: accent,
              then: "text-purple",
            })}
          >
            {accent && icon && <RyeIcon name={icon} size="sm" />}
            <h2
              className={buildClasses("font-medium", {
                if: accent,
                then: "text-sm",
                else: "text-on-surface-800",
              })}
            >
              {title}
            </h2>
          </div>
          {trailing != null && (
            <div className={buildClasses("shrink-0")}>{trailing}</div>
          )}
        </div>
      )}
      {fillHeight ? (
        <div className={buildClasses("flex-1", "flex", "flex-col")}>
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  );
}
